import { useEffect, useRef, RefObject, useCallback } from 'react';

interface UseModalFocusProps {
  isOpen: boolean;
  onClose?: () => void;
  modalRef?: RefObject<HTMLElement>;
  triggerRef?: RefObject<HTMLElement>; // Element that opened the modal
  initialFocusRef?: RefObject<HTMLElement>;
}

export function useModalFocus({
  isOpen,
  onClose,
  modalRef,
  triggerRef,
  initialFocusRef,
}: UseModalFocusProps) {
  const firstFocusableRef = useRef<HTMLElement>(null);
  const lastFocusableRef = useRef<HTMLElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  // Get all focusable elements within the modal
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!modalRef?.current) return [];
    
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ];
    
    return Array.from(
      modalRef.current.querySelectorAll(focusableSelectors.join(', '))
    ) as HTMLElement[];
  }, [modalRef]);

  // Store the currently focused element when modal opens
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Set initial focus to first focusable element
  useEffect(() => {
    if (isOpen && modalRef?.current) {
      const focusableElements = getFocusableElements();
      
      if (focusableElements.length > 0) {
        firstFocusableRef.current = focusableElements[0];
        lastFocusableRef.current = focusableElements[focusableElements.length - 1];
      }

      const initialFocusElement = initialFocusRef?.current || firstFocusableRef.current;

      if (initialFocusElement) {
        initialFocusElement.focus();
      }
    }
  }, [isOpen, modalRef, getFocusableElements, initialFocusRef]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      // Use setTimeout to ensure the modal is fully closed before focusing
      setTimeout(() => {
        if (triggerRef?.current) {
          // If we have a specific trigger element, focus that
          triggerRef.current.focus();
        } else if (previousActiveElement.current) {
          // Otherwise, focus the element that was active before
          previousActiveElement.current.focus();
        }
      }, 0);
    }
  }, [isOpen, triggerRef]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Handle focus trapping
  useEffect(() => {
    const handleTabKey = (event: KeyboardEvent) => {
      if (!isOpen || !modalRef?.current) return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // If shift + tab pressed and first element is focused, focus last element
      if (event.shiftKey && event.key === 'Tab' && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // If tab pressed and last element is focused, focus first element
      else if (!event.shiftKey && event.key === 'Tab' && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleTabKey);
    }

    return () => {
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, modalRef, getFocusableElements]);

  return {
    firstFocusableRef,
    lastFocusableRef
  };
} 